import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, AssetFolder } from '../api/client';
import { Skeleton } from '../components/Layout';
import { pageNumbers } from '../lib/paginate';

const PAGE_SIZE = 50;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}


export default function AssetArchivePage() {
  const [prefixes, setPrefixes] = useState<string[]>([]);
  const [indexLoaded, setIndexLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [folders, setFolders] = useState<AssetFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingPrefix, setDownloadingPrefix] = useState<string | null>(null);
  const loadedRef = useRef(false);
  const pageCache = useRef(new Map<number, AssetFolder[]>());
  const reqRef = useRef(0);

  useEffect(() => {
    if (loadedRef.current) return; // StrictMode double-mount guard
    loadedRef.current = true;
    api.listAssetArchive()
      .then((r) => {
        setPrefixes(r.prefixes);
        setIndexLoaded(true);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!indexLoaded) return;
    const cached = pageCache.current.get(page);
    if (cached) {
      setFolders(cached);
      setLoading(false);
      return;
    }
    const slice = prefixes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    if (!slice.length) {
      setFolders([]);
      setLoading(false);
      return;
    }
    const reqId = ++reqRef.current;
    setLoading(true);
    setError('');
    api.assetArchiveDetails(slice)
      .then((r) => {
        pageCache.current.set(page, r.folders);
        if (reqId === reqRef.current) setFolders(r.folders);
      })
      .catch((e: Error) => {
        if (reqId === reqRef.current) setError(e.message);
      })
      .finally(() => {
        if (reqId === reqRef.current) setLoading(false);
      });
  }, [indexLoaded, page, prefixes]);

  const download = async (folder: AssetFolder) => {
    setDownloadingPrefix(folder.prefix);
    setError('');
    try {
      const { url } = await api.downloadAssetFolder(folder.prefix);
      window.location.href = url;
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setDownloadingPrefix(null);
    }
  };

  const total = prefixes.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goTo = (p: number) => {
    setPage(Math.min(Math.max(1, p), totalPages));
    window.scrollTo({ top: 0 });
  };
  const rangeStart = (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const pagination = indexLoaded && totalPages > 1 && (
    <nav className="pagination" aria-label="Pagination">
      <button type="button" className="btn ghost" onClick={() => goTo(page - 1)} disabled={page === 1 || loading}>
        Prev
      </button>
      {pageNumbers(page, totalPages).map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="pagination-gap muted">…</span>
        ) : (
          <button
            key={p}
            type="button"
            className={`btn ghost pagination-page${p === page ? ' active' : ''}`}
            onClick={() => goTo(p)}
            disabled={loading}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        ),
      )}
      <button type="button" className="btn ghost" onClick={() => goTo(page + 1)} disabled={page === totalPages || loading}>
        Next
      </button>
    </nav>
  );

  return (
    <div>
      <div className="asset-archive-header">
        <h1>Asset Archive</h1>
        {indexLoaded && total > 0 && (
          <p className="muted">
            Showing {rangeStart}–{rangeEnd} of {total} assets
          </p>
        )}
      </div>
      <p className="asset-archive-disclaimer muted">
        <span className="asset-archive-disclaimer-icon" aria-hidden>⚠️</span>
        <span>
          This is a read-only archive from our Asset Library. You may use any of these in your
          games and prototypes. Some are UnityPackages and may need to be{' '}
          <Link to="/dashboard/dd3d1051-1643-4e6c-b31c-3f3748999fde">extracted</Link>.
        </span>
      </p>
      {error && <p className="error">{error}</p>}

      <div className="asset-list">
        {loading
          ? Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="card asset-row">
                <Skeleton width="220px" height="150px" radius="6px" style={{ flexShrink: 0 }} />
                <div className="asset-row-body">
                  <Skeleton width="40%" height="0.95rem" style={{ marginBottom: '0.35rem' }} />
                  <Skeleton width="20%" height="0.75rem" />
                </div>
              </div>
            ))
          : folders.map((f) => (
              <div key={f.prefix} className="card asset-row">
                {f.thumbnailUrl ? (
                  <img src={f.thumbnailUrl} alt={f.name} loading="lazy" className="asset-row-thumb" />
                ) : (
                  <div className="asset-row-thumb asset-row-thumb-empty muted">No preview</div>
                )}
                <div className="asset-row-body">
                  <h3 className="asset-row-name" title={f.name}>{f.name}</h3>
                  {(f.formats ?? []).length > 0 && (
                    <div className="tag-chips asset-row-formats">
                      {f.formats.map((ext) => <span key={ext} className="chip">.{ext}</span>)}
                    </div>
                  )}
                  <p className="muted">
                    {f.fileCount} {f.fileCount === 1 ? 'file' : 'files'} · {formatBytes(f.totalBytes)}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => download(f)}
                  disabled={downloadingPrefix !== null}
                >
                  {downloadingPrefix === f.prefix ? 'Preparing…' : 'Download'}
                </button>
              </div>
            ))}
        {!loading && indexLoaded && !total && !error && (
          <p className="muted">The archive is empty.</p>
        )}
      </div>

      {pagination}
    </div>
  );
}
